import fetchApiWithAuth from "./fetch";

export type AgentSession = {
  id: number;
  title: string;
  user_id: number | null;
  context: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

export type AgentMessage = {
  id: number;
  session_id: number;
  role: string;
  content: string;
  name: string | null;
  created_at: string;
};

export type AgentProposal = {
  id: number;
  kind: string;
  target: Record<string, unknown>;
  patch: Record<string, unknown>;
  summary: string;
  status: string;
  error: string | null;
};

export type AgentRun = {
  id: number;
  session_id: number | null;
  status: string;
  assistant_message: string | null;
  error: string | null;
  steps: number;
  created_at: string;
  finished_at: string | null;
};

export const isAgentRunInProgress = (status?: string | null) =>
  status === "pending" || status === "running";

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const getAgentRun = async (id: number): Promise<AgentRun | null> => {
  const res = await fetchApiWithAuth(`/api/agent/runs/${id}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

export const waitForAgentRun = async (
  runId: number,
  opts?: { intervalMs?: number; timeoutMs?: number; isCancelled?: () => boolean }
): Promise<AgentRun | null> => {
  const intervalMs = opts?.intervalMs ?? 2000;
  const timeoutMs = opts?.timeoutMs ?? 15 * 60 * 1000;
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (opts?.isCancelled?.()) return null;
    const run = await getAgentRun(runId);
    if (!run) throw new Error("run not found");
    if (!isAgentRunInProgress(run.status)) return run;
    await sleep(intervalMs);
  }
  throw new Error("Модель всё ещё думает. Обновите чат через минуту.");
};

export const listAgentSessions = async (): Promise<AgentSession[]> => {
  const res = await fetchApiWithAuth("/api/agent/sessions");
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

export const createAgentSession = async (body?: {
  title?: string;
  context?: Record<string, unknown>;
}): Promise<AgentSession> => {
  const res = await fetchApiWithAuth("/api/agent/sessions", {
    method: "POST",
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

export const getAgentSession = async (
  id: number
): Promise<{
  session: AgentSession;
  messages: AgentMessage[];
  proposals: AgentProposal[];
  latestRun: AgentRun | null;
}> => {
  const res = await fetchApiWithAuth(`/api/agent/sessions/${id}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

export const postAgentMessage = async (
  sessionId: number,
  content: string,
  dryRun?: boolean
): Promise<{ run: AgentRun; accepted: true }> => {
  const res = await fetchApiWithAuth(`/api/agent/sessions/${sessionId}/messages`, {
    method: "POST",
    body: JSON.stringify({ content, dryRun }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  return res.json();
};

export const approveAgentProposal = async (id: number) => {
  const res = await fetchApiWithAuth(`/api/agent/proposals/${id}/approve`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

export const rejectAgentProposal = async (id: number) => {
  const res = await fetchApiWithAuth(`/api/agent/proposals/${id}/reject`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

export const uploadAgentCatalogImport = async (
  sessionId: number,
  file: File
) => uploadAgentAttachment(sessionId, file);

export const uploadAgentAttachment = async (
  sessionId: number,
  file: File
) => {
  const form = new FormData();
  form.append("file", file);
  const res = await fetchApiWithAuth(
    `/api/agent/sessions/${sessionId}/uploads`,
    {
      method: "POST",
      body: form,
    },
    true
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};
